const DEFAULT_API_URL = "http://localhost:8080/api";

type ProcessEnv = Record<string, string | undefined>;

const hasProcessEnv = (): boolean => {
  try {
    return (
      typeof process !== "undefined" &&
      !!process &&
      typeof process === "object" &&
      typeof process.env === "object"
    );
  } catch (error) {
    return false;
  }
};

const captureEnvValue = (
  reader: () => string | undefined,
): string | undefined => {
  try {
    return reader();
  } catch (error) {
    return undefined;
  }
};

const CAPTURED_ENV: ProcessEnv = {
  REACT_APP_API_URL: captureEnvValue(() => process.env.REACT_APP_API_URL),
  REACT_APP_ZAMMAD_FORM: captureEnvValue(
    () => process.env.REACT_APP_ZAMMAD_FORM,
  ),
  GOOGLE_MAPS_API_KEY: captureEnvValue(() => process.env.GOOGLE_MAPS_API_KEY),
  REACT_APP_API_KEY: captureEnvValue(() => process.env.REACT_APP_API_KEY),
};

const getEnvValue = (value: string | undefined): string | undefined =>
  typeof value === "string" ? value : undefined;

const processEnvAvailable = hasProcessEnv();
const runtimeEnv: ProcessEnv | null = processEnvAvailable
  ? (process.env as ProcessEnv)
  : null;

const pickEnvValue = <K extends keyof typeof CAPTURED_ENV>(
  key: K,
): string | undefined => {
  const runtimeValue = runtimeEnv ? runtimeEnv[key] : undefined;
  const capturedValue = CAPTURED_ENV[key];

  return getEnvValue(runtimeValue) ?? getEnvValue(capturedValue);
};

const stripTrailingSlash = (value: string) => value.replace(/\/+$/, "");

const parseHostname = (value: string | null) => {
  try {
    return value ? new URL(value).hostname : null;
  } catch (error) {
    return null;
  }
};

const resolveDefaultApiForBrowser = () => {
  const origin = resolveBrowserOrigin();
  if (origin) {
    return stripTrailingSlash(`${origin}/api`);
  }

  return stripTrailingSlash(DEFAULT_API_URL);
};

const resolveBrowserOrigin = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const { origin } = window.location || {};
  return origin && origin.trim().length ? origin : null;
};

const normaliseApiUrl = (value: string | undefined | null): string => {
  const trimmed = (value || "").trim();
  const browserOrigin = resolveBrowserOrigin();

  if (!trimmed.length) {
    return resolveDefaultApiForBrowser();
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return stripTrailingSlash(trimmed);
  }

  if (!browserOrigin) {
    return stripTrailingSlash(DEFAULT_API_URL);
  }

  if (trimmed.startsWith("/")) {
    return stripTrailingSlash(`${browserOrigin}${trimmed}`);
  }

  return stripTrailingSlash(`${browserOrigin}/${trimmed}`);
};

export const API_URL: string = normaliseApiUrl(pickEnvValue("REACT_APP_API_URL"));
export const ZAMMAD_FORM =
  pickEnvValue("REACT_APP_ZAMMAD_FORM") ||
  "https://support.cockpit-it.com/assets/form/form.js";
export const LEGACY_GOOGLE_MAPS_API_KEY = (() => {
  const candidates = [
    pickEnvValue("GOOGLE_MAPS_API_KEY"),
    pickEnvValue("REACT_APP_API_KEY"),
  ];

  for (const candidate of candidates) {
    const trimmed = (candidate || "").trim();

    if (trimmed.length) {
      return trimmed;
    }
  }

  return null;
})();
